import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import axios from 'axios';
import Login from '@/pages/Login';
import Dashboard from '@/pages/Dashboard';
import ManagementDashboard from '@/pages/ManagementDashboard';
import Transactions from '@/pages/Transactions';
import Customers from '@/pages/Customers';
import Suppliers from '@/pages/Suppliers';
import WhatsAppInbox from '@/pages/WhatsAppInbox';
import Reconciliation from '@/pages/Reconciliation';
import BankStatements from '@/pages/BankStatements';
import Reports from '@/pages/Reports';
import ChartOfAccounts from '@/pages/ChartOfAccounts';
import Settings from '@/pages/Settings';
import Sales from '@/pages/Sales';
import PurchaseBook from '@/pages/PurchaseBook';
import SuperAdminDashboard from '@/pages/SuperAdminDashboard';
import Layout from '@/components/Layout';
import MobileLayout from '@/components/MobileLayout';
import MobileInbox from '@/pages/MobileInbox';
import { Toaster } from '@/components/ui/sonner';
import '@/App.css';

import { supabase } from './supabaseClient';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const AuthContext = React.createContext();

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Axios Interceptors (Log errors)
    axios.interceptors.response.use(
      response => response,
      error => {
        console.error("Global Axios Error:", error);
        return Promise.reject(error);
      }
    );

    // Initialize Session
    const initSession = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      handleSession(session);
    };

    initSession();

    // Listen for Auth Changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      handleSession(session);
    });

    return () => subscription.unsubscribe();
  }, []);

  const handleSession = async (session) => {
    if (session) {
      // Set Global Header
      axios.defaults.headers.common['Authorization'] = `Bearer ${session.access_token}`;

      // Fetch User Profile from Backend (Enriched with Company ID, Role, etc.)
      try {
        const response = await axios.get(`${API}/auth/me`);
        setUser(response.data);
      } catch (error) {
        console.error("Error fetching user profile:", error);
        // If backend rejects token, signOut?
        if (error.response && error.response.status === 401) {
          supabase.auth.signOut();
        }
      }
    } else {
      // Clear
      delete axios.defaults.headers.common['Authorization'];
      setUser(null);
    }
    setLoading(false);
  };

  const login = async () => {
    // Login is handled by Login.jsx via supabase directly, allowing the listener here to react.
    // We can expose supabase helper or just let components use supabase directly.
    // Current usage expects `login(token, user)` but we'll deprecated that.
  };

  const logout = async () => {
    await supabase.auth.signOut();
  };

  if (loading) {
    return <div className="flex items-center justify-center h-screen">Cargando...</div>;
  }

  return (
    <AuthContext.Provider value={{ user, login, logout, API }}>
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={user ? <Navigate to="/" /> : <Login />} />
          <Route path="/" element={user ? <Layout /> : <Navigate to="/login" />}>
            <Route index element={<Dashboard />} />
            <Route path="management" element={<ManagementDashboard />} />
            <Route path="transactions" element={<Transactions />} />
            <Route path="customers" element={<Customers />} />
            <Route path="suppliers" element={<Suppliers />} />
            <Route path="inbox" element={<WhatsAppInbox />} />
            <Route path="reconciliation" element={<Reconciliation />} />
            <Route path="bank-statements" element={<BankStatements />} />
            <Route path="reports" element={<Reports />} />
            <Route path="chart-of-accounts" element={<ChartOfAccounts />} />
            <Route path="settings" element={<Settings />} />
            <Route path="sales" element={<Sales />} />
            <Route path="purchase-book" element={<PurchaseBook />} />
            <Route path="admin" element={<SuperAdminDashboard />} />
          </Route>

          {/* Mobile Routes */}
          <Route path="/mobile" element={user ? <MobileLayout /> : <Navigate to="/login" />}>
            <Route index element={<MobileInbox />} />
            <Route path="upload" element={<MobileInbox />} /> {/* Reuse for now */}
          </Route>
        </Routes>
      </BrowserRouter>
      <Toaster position="top-right" />
    </AuthContext.Provider>
  );
}

export default App;
